import React, { useState } from 'react';
import {
  Box,
  Typography,
  List,
  ListItem,
  ListItemAvatar,
  ListItemText,
  Avatar,
  IconButton,
  Tooltip,
  CircularProgress,
  Alert
} from '@mui/material';
import { ChatCircle as ChatIcon } from '@phosphor-icons/react';
import api from '../../utils/api';
import useThemeStyles from '../../theme/useThemeStyles';
import { useNavigate } from 'react-router-dom';

const SupplierList = ({ weddingId, currentBusinessId, onChatCreated }) => {
  const _styles = useThemeStyles();
  const navigate = useNavigate();
  const [suppliers, setSuppliers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [creatingChat, setCreatingChat] = useState(null);

  React.useEffect(() => {
    if (weddingId) {
      fetchSuppliers();
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps -- verified intentional deps
  }, [weddingId]);

  const fetchSuppliers = async () => {
    try {
      setLoading(true);
      setError('');
      const response = await api.get(`/api/supplier-planning/wedding-suppliers/${weddingId}`);
      // Filter out current business
      const filtered = (response.data || []).filter(
        supplier => supplier._id !== currentBusinessId
      );
      setSuppliers(filtered);
    } catch (err) {
      setError('Failed to load suppliers');
    } finally {
      setLoading(false);
    }
  };

  const handleStartChat = async (supplierBusinessId) => {
    try {
      setCreatingChat(supplierBusinessId);
      const response = await api.post('/api/chat/conversations', {
        type: 'supplier-supplier',
        secondBusinessId: supplierBusinessId,
        weddingId: weddingId,
      });

      if (onChatCreated) {
        onChatCreated(response.data);
      } else {
        // Navigate to chat
        navigate(`/supplier/messages/${response.data._id}`);
      }
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to start chat');
    } finally {
      setCreatingChat(null);
    }
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
        <CircularProgress size={40} />
      </Box>
    );
  }

  if (error && !suppliers.length) {
    return (
      <Alert severity="error" sx={{ mb: 2 }}>
        {error}
      </Alert>
    );
  }

  if (suppliers.length === 0) {
    return (
      <Box sx={{ py: 2, textAlign: 'center', color: 'text.secondary' }}>
        <Typography variant="body2">
          No other suppliers have access to this wedding yet.
        </Typography>
      </Box>
    );
  }

  return (
    <Box>
      <Typography variant="h6" sx={{ mb: 2, fontWeight: 600 }}>
        Other Suppliers
      </Typography>
      <Typography variant="body2" sx={{ mb: 2, color: 'text.secondary' }}>
        Chat with other suppliers working on this wedding
      </Typography>
      
      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>
          {error}
        </Alert>
      )}

      <List sx={{ p: 0 }}>
        {suppliers.map((supplier) => (
          <ListItem
            key={supplier._id}
            sx={{
              borderRadius: '12px',
              mb: 1,
              backgroundColor: '#fff',
              border: '1px solid rgba(0, 0, 0, 0.08)',
              '&:hover': {
                backgroundColor: 'rgba(239, 65, 146, 0.04)',
              },
            }}
            secondaryAction={
              <Tooltip title="Start chat">
                <span>
                  <IconButton
                    edge="end"
                    onClick={() => handleStartChat(supplier._id)}
                    disabled={creatingChat === supplier._id}
                    sx={{
                      color: '#ef4192',
                      '&:hover': {
                        backgroundColor: 'rgba(239, 65, 146, 0.1)',
                      },
                    }}
                  >
                    {creatingChat === supplier._id ? (
                      <CircularProgress size={20} />
                    ) : (
                      <ChatIcon size={20} />
                    )}
                  </IconButton>
                </span>
              </Tooltip>
            }
          >
            <ListItemAvatar>
              <Avatar
                src={supplier.media?.logo}
                sx={{
                  bgcolor: '#ffffff',
                  color: '#000000',
                  border: '1px solid rgba(0, 0, 0, 0.1)',
                  width: 40,
                  height: 40,
                }}
              >
                {supplier.businessName?.[0]?.toUpperCase() || 'S'}
              </Avatar>
            </ListItemAvatar>
            <ListItemText
              primary={
                <Typography variant="subtitle2" sx={{ fontWeight: 500 }}>
                  {supplier.businessName || 'Supplier'}
                </Typography>
              }
              secondary={
                <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                  {supplier.category || 'Supplier'}
                </Typography>
              }
            />
          </ListItem>
        ))}
      </List>
    </Box>
  );
};

export default SupplierList;

